import InfiniteScroll from 'react-infinite-scroller'
import styled from 'styled-components'
import ActivityItem from '../../../../components/profile/activity/ActivityItem'
import { NoVideosYet } from '../../../../components/video/common'
import { fbEnrichUsersFromIds, fbEnrichVideosFromIds } from '../../../../data/FirebaseEnrich'
import { Query_Root, Receipt } from '../../../../graphql/indexer-graphql-types'
import { useInfiniteLoadIndexer } from '../../../../hooks/infiniteLoadIndexer'
import { VideoData } from '../../../../interfaces'
import { FbEnrichedUser } from '../../../../interfaces/User'
import { receiptComparer } from '../../../../utils/comparer'
import { gql } from '@apollo/client'
// import useGlobalActivityFeed from '../application/useGlobalActivityFeed'

/*================================ Types =====================================*/

/**
 * The query to fetch all receipts.
 */
export const ALL_RECEIPTS_QUERY = gql`
  query GetAllReceipts($offset: Int, $limit: Int) {
    receipt(offset: $offset, limit: $limit, orderBy: { createdAt: DESC }) {
      receiptType
      buyer
      seller
      price
      createdAt
      tokenSize
      mint
    }
  }
`

/*============================== Functions ====================================*/

export type EnrichedReceipt = Omit<Receipt, 'seller' | 'buyer'> & {
  seller: FbEnrichedUser
  buyer: FbEnrichedUser
  video: VideoData
}

const parseItems = (data: Query_Root | undefined): Receipt[] => {
  if (!data) {
    return []
  }
  return (data.receipt || []) as Receipt[]
}

/**
 * Enriches the receipts with the seller, buyer and video data.
 */
const enrichItems = async (receipts: Receipt[]): Promise<EnrichedReceipt[]> => {
  if (!receipts.length) {
    return []
  }

  const buyerIds: string[] = receipts.map((receipt) => receipt.buyer).filter(Boolean) as string[]
  const sellerIds: string[] = receipts.map((receipt) => receipt.seller).filter(Boolean) as string[]
  const videoIds: string[] = receipts.map((receipt) => receipt.mint).filter(Boolean) as string[]
  const [buyers, sellers, videos] = await Promise.all([
    fbEnrichUsersFromIds(buyerIds),
    fbEnrichUsersFromIds(sellerIds),
    fbEnrichVideosFromIds(videoIds),
  ])

  return receipts.map(
    (receipt) =>
      ({
        ...receipt,
        seller: sellers.find((user) => user.address === receipt.seller),
        buyer: buyers.find((user) => user.address === receipt.buyer),
        video: videos.find((video) => video.address === receipt.mint),
      } as EnrichedReceipt)
  )
}

/*======================== React component ===================================*/

const GlobalyActivityFeedSection = (): JSX.Element => {
  const { data: items, hasNext } = useInfiniteLoadIndexer<EnrichedReceipt, Receipt>({
    id: '',
    query: ALL_RECEIPTS_QUERY,
    enrichItems,
    parseItems,
    comparer: receiptComparer,
    page_size: 5,
    variables: {},
    refetchMarker: 'activity',
  })

  if (items.length === 0) {
    return hasNext ? (
      <StyledSkeletonContainer>
        <Title>RECENT ACTIVITY</Title>
        {[...Array(1).keys()].map((index) => (
          <ActivityItem key={`skeleton-item-${index}`} hideTimestamp />
        ))}
      </StyledSkeletonContainer>
    ) : (
      <NoVideosYet>{`No activity yet.`}</NoVideosYet>
    )
  }

  return (
    <StyledInfiniteScroll hasMore={hasNext} useWindow>
      <TitleDiv>
        <Title>RECENT ACTIVITY</Title>
      </TitleDiv>
      {items.map(
        (receipt, i) =>
          receipt &&
          receipt.video !== undefined && (
            <ActivityItemWrapper>
              <ActivityItem receipt={receipt} key={`activity-item-${i}`} hideTimestamp />
            </ActivityItemWrapper>
          )
      )}
    </StyledInfiniteScroll>
  )
}

export default GlobalyActivityFeedSection

/*======================== Styled components =================================*/

const itemsContainerStyle = `
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  overflow-y: hidden;
  width: calc(100% - 40px);
  margin: 25px 0 0 0;
  // margin-bottom: 80px;

  // tablet
  @media (min-width: 43em) {
    width: calc(100% - 50px);
  }

  // small desktop
  @media (min-width: 62em) {
    width: 88%;
  }

  // large desktop
  @media (min-width: 82em) {
    max-width: 1800px;
  }
`

const StyledSkeletonContainer = styled.div`
  ${itemsContainerStyle}
`

const StyledInfiniteScroll = styled(InfiniteScroll)`
  ${itemsContainerStyle}
`

const TitleDiv = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`

// const OuterDiv = styled.div`
//   display: flex;
//   flex-direction: column;
//   align-items: center;
//   height: 100%;
//   /* max-width: 750px; */
//   width: 100%;
//   overflow-y: hidden;
// `

const Title = styled.div<{ isHome?: boolean }>`
  font-size: 30px;
  font-weight: 600;
  white-space: pre-wrap;
  text-align: left;
  width: calc(100% - 40px);

  @media (min-width: 43em) {
    /* margin: ${(props) => (props.isHome ? `5px 20px 20px` : `0`)}; */
    font-size: 48px;
  }

  @media (min-width: 62em) {
    margin: ${(props) => (props.isHome ? `5px 20px 20px` : `5px 0 20px`)};
    font-size: 42px;
    text-align: left;
  }

  @media (min-width: 82em) {
    font-size: 54px;
  }
`

const ActivityItemWrapper = styled.div`
  width: 100%;
`
